import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { safeStorageGet, safeStorageSet } from '../utils/safeStorage';

export type Language = 'en' | 'es' | 'fr' | 'de' | 'it' | 'pt' | 'zh' | 'ja';

interface Translations {
  [key: string]: string;
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  languages: { code: Language; name: string; nativeName: string }[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const languages: { code: Language; name: string; nativeName: string }[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
];

const translations: Record<Language, Translations> = {
  en: {
    // Navigation
    'nav.locate': 'Locate',
    'nav.humidor': 'Humidor',
    'nav.log': 'Log',
    'nav.wishlist': 'Wishlist',
    'nav.analytics': 'Analytics',
    'nav.journey': 'Journey',
    'nav.sommelier': 'Sommelier',
    'nav.community': 'Community',
    'nav.profile': 'Profile',
    'nav.signOut': 'Sign Out',
    'nav.gallery': 'Gallery',

    // Dashboard
    'dashboard.welcome': 'Welcome back',
    'dashboard.quickActions': 'Quick Actions',
    'dashboard.recentSmokes': 'Recent Smokes',
    'dashboard.noLogs': 'No smokes logged yet',

    // Cigar Logger
    'logger.title': 'Log Your Experience',
    'logger.editTitle': 'Edit Log Entry',
    'logger.subtitle': 'Track your journey through the leaf.',
    'logger.editSubtitle': 'Update the details of your smoke.',
    'logger.brand': 'Brand',
    'logger.name': 'Name / Vitola',
    'logger.wrapper': 'Wrapper',
    'logger.origin': 'Origin',
    'logger.price': 'Price',
    'logger.location': 'Location',
    'logger.rating': 'Rating',
    'logger.notes': 'Tasting Notes',
    'logger.history': 'History / Background',
    'logger.save': 'Save to Logbook',
    'logger.update': 'Update Log',
    'logger.cancel': 'Cancel',
    'logger.autoFill': 'Auto-Fill Details',
    'logger.autoGenerate': 'Auto-Generate with AI',
    'logger.uploadPhoto': 'Upload Cigar Photo',
    'logger.identifyLabel': 'Identify Label',
    'logger.analyzing': 'Analyzing...',
    'logger.saving': 'Saving...',
    'logger.myJournal': 'My Humidor Journal',
    'logger.noLogs': 'No smokes logged yet.',

    // Humidor
    'humidor.title': 'Digital Humidor',
    'humidor.subtitle': 'Manage your personal collection and aging.',
    'humidor.addStick': 'Add Stick',
    'humidor.addToCollection': 'Add to Collection',
    'humidor.empty': 'Your humidor is empty. Start building your collection!',
    'humidor.loading': 'Loading your collection...',
    'humidor.smokeOne': 'Smoke One',
    'humidor.startAging': 'Start Aging',
    'humidor.markComplete': 'Mark Complete',
    'humidor.aging': 'Aging',
    'humidor.ready': 'Ready!',

    // Community
    'community.title': 'Community Feed',
    'community.subtitle': 'See what others are smoking right now.',
    'community.anonymous': 'Anonymous Aficionado',
    'community.comment': 'Comment',
    'community.addedToWishlist': 'Added to Wishlist!',
    'community.feedUpdates': 'Feed updates in real-time.',

    // Profile
    'profile.clubMember': 'Club Member',
    'profile.logs': 'Logs',
    'profile.totalLogged': 'Total cigars recorded',
    'profile.avgRating': 'Avg Rating',
    'profile.basedOnHistory': 'Based on your history',
    'profile.favorite': 'Favorite',
    'profile.mostLogged': 'Most logged brand',
    'profile.membership': 'Membership & Billing',
    'profile.active': 'Active',
    'profile.inactive': 'Inactive',
    'profile.currentPlan': 'Current Plan',
    'profile.nextRenewal': 'Next Renewal',
    'profile.paymentMethod': 'Payment Method',
    'profile.billingHistory': 'View Billing History',
    'profile.cancelSubscription': 'Cancel Subscription',

    // Search
    'search.placeholder': 'Search cigars...',
    'search.noResults': 'No results found',
    'search.filters': 'Filters',
    'search.clearFilters': 'Clear Filters',

    // Gallery
    'gallery.title': 'Photo Gallery',
    'gallery.subtitle': 'Your cigar photography collection',
    'gallery.noPhotos': 'No photos yet. Start logging cigars with photos!',

    // Notifications
    'notifications.title': 'Notifications',
    'notifications.empty': 'No notifications',
    'notifications.markAllRead': 'Mark all as read',
    'notifications.newLike': 'liked your smoke',
    'notifications.newComment': 'commented on your smoke',
    'notifications.newFollower': 'started following you',
    'notifications.agingComplete': 'is ready to smoke!',

    // Common
    'common.loading': 'Loading...',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.close': 'Close',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.all': 'All',
    'common.settings': 'Settings',
    'common.theme': 'Theme',
    'common.language': 'Language',
    'common.dark': 'Dark',
    'common.light': 'Light',

    // Offline
    'offline.notice': 'You are offline',
    'offline.pendingSync': 'Changes will sync when online',
    'offline.syncing': 'Syncing...',
    'offline.synced': 'All changes synced',

    // Follow
    'follow.follow': 'Follow',
    'follow.following': 'Following',
    'follow.followers': 'Followers',
    'follow.unfollow': 'Unfollow',

    // Collection Sharing
    'sharing.shareCollection': 'Share Collection',
    'sharing.publicProfile': 'Public Profile',
    'sharing.privateProfile': 'Private Profile',
    'sharing.compare': 'Compare Collections',

    // Flavor Profile
    'flavor.title': 'Your Flavor Profile',
    'flavor.preferences': 'Based on your smoking history',
    'flavor.recommended': 'Recommended for You',
    'flavor.similar': 'Similar to your favorites',
  },
  es: {
    // Navigation
    'nav.locate': 'Localizar',
    'nav.humidor': 'Humidor',
    'nav.log': 'Registrar',
    'nav.wishlist': 'Favoritos',
    'nav.analytics': 'Análisis',
    'nav.journey': 'Viaje',
    'nav.sommelier': 'Sommelier',
    'nav.community': 'Comunidad',
    'nav.profile': 'Perfil',
    'nav.signOut': 'Cerrar Sesión',
    'nav.gallery': 'Galería',

    // Dashboard
    'dashboard.welcome': 'Bienvenido de nuevo',
    'dashboard.quickActions': 'Acciones Rápidas',
    'dashboard.recentSmokes': 'Fumadas Recientes',
    'dashboard.noLogs': 'Aún no hay registros',

    // Cigar Logger
    'logger.title': 'Registra tu Experiencia',
    'logger.editTitle': 'Editar Registro',
    'logger.subtitle': 'Sigue tu viaje a través de la hoja.',
    'logger.editSubtitle': 'Actualiza los detalles de tu fumada.',
    'logger.brand': 'Marca',
    'logger.name': 'Nombre / Vitola',
    'logger.wrapper': 'Capa',
    'logger.origin': 'Origen',
    'logger.price': 'Precio',
    'logger.location': 'Ubicación',
    'logger.rating': 'Puntuación',
    'logger.notes': 'Notas de Cata',
    'logger.history': 'Historia / Antecedentes',
    'logger.save': 'Guardar en Registro',
    'logger.update': 'Actualizar Registro',
    'logger.cancel': 'Cancelar',
    'logger.autoFill': 'Auto-Completar',
    'logger.autoGenerate': 'Generar con IA',
    'logger.uploadPhoto': 'Subir Foto del Puro',
    'logger.identifyLabel': 'Identificar Etiqueta',
    'logger.analyzing': 'Analizando...',
    'logger.saving': 'Guardando...',
    'logger.myJournal': 'Mi Diario de Puros',
    'logger.noLogs': 'Aún no hay registros.',

    // Common
    'common.loading': 'Cargando...',
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.close': 'Cerrar',
    'common.search': 'Buscar',
    'common.filter': 'Filtrar',
    'common.all': 'Todo',
    'common.settings': 'Configuración',
    'common.theme': 'Tema',
    'common.language': 'Idioma',
    'common.dark': 'Oscuro',
    'common.light': 'Claro',

    // Humidor
    'humidor.title': 'Humidor Digital',
    'humidor.subtitle': 'Gestiona tu colección personal.',
    'humidor.addStick': 'Añadir Puro',
    'humidor.addToCollection': 'Añadir a la Colección',
    'humidor.empty': '¡Tu humidor está vacío. Comienza tu colección!',
    'humidor.loading': 'Cargando tu colección...',
    'humidor.smokeOne': 'Fumar Uno',
    'humidor.startAging': 'Iniciar Añejamiento',
    'humidor.markComplete': 'Marcar Completo',
    'humidor.aging': 'Añejamiento',
    'humidor.ready': '¡Listo!',

    // Community
    'community.title': 'Feed de la Comunidad',
    'community.subtitle': 'Ve lo que otros están fumando.',
    'community.anonymous': 'Aficionado Anónimo',
    'community.comment': 'Comentar',
    'community.addedToWishlist': '¡Añadido a Favoritos!',
    'community.feedUpdates': 'El feed se actualiza en tiempo real.',

    // Offline
    'offline.notice': 'Estás sin conexión',
    'offline.pendingSync': 'Los cambios se sincronizarán cuando estés en línea',
    'offline.syncing': 'Sincronizando...',
    'offline.synced': 'Todos los cambios sincronizados',

    // Follow
    'follow.follow': 'Seguir',
    'follow.following': 'Siguiendo',
    'follow.followers': 'Seguidores',
    'follow.unfollow': 'Dejar de seguir',

    // Notifications
    'notifications.title': 'Notificaciones',
    'notifications.empty': 'Sin notificaciones',
    'notifications.markAllRead': 'Marcar todo como leído',
    'notifications.newLike': 'le gustó tu fumada',
    'notifications.newComment': 'comentó en tu fumada',
    'notifications.newFollower': 'comenzó a seguirte',
    'notifications.agingComplete': '¡está listo para fumar!',

    // Gallery
    'gallery.title': 'Galería de Fotos',
    'gallery.subtitle': 'Tu colección de fotografías de puros',
    'gallery.noPhotos': 'Sin fotos aún. ¡Comienza a registrar puros con fotos!',

    // Profile
    'profile.clubMember': 'Miembro del Club',
    'profile.logs': 'Registros',
    'profile.totalLogged': 'Total de puros registrados',
    'profile.avgRating': 'Puntuación Promedio',
    'profile.basedOnHistory': 'Basado en tu historial',
    'profile.favorite': 'Favorito',
    'profile.mostLogged': 'Marca más registrada',
    'profile.membership': 'Membresía y Facturación',
    'profile.active': 'Activo',
    'profile.inactive': 'Inactivo',
    'profile.currentPlan': 'Plan Actual',
    'profile.nextRenewal': 'Próxima Renovación',
    'profile.paymentMethod': 'Método de Pago',
    'profile.billingHistory': 'Ver Historial de Facturación',
    'profile.cancelSubscription': 'Cancelar Suscripción',

    // Search
    'search.placeholder': 'Buscar puros...',
    'search.noResults': 'Sin resultados',
    'search.filters': 'Filtros',
    'search.clearFilters': 'Limpiar Filtros',

    // Sharing
    'sharing.shareCollection': 'Compartir Colección',
    'sharing.publicProfile': 'Perfil Público',
    'sharing.privateProfile': 'Perfil Privado',
    'sharing.compare': 'Comparar Colecciones',

    // Flavor Profile
    'flavor.title': 'Tu Perfil de Sabores',
    'flavor.preferences': 'Basado en tu historial de fumadas',
    'flavor.recommended': 'Recomendado para Ti',
    'flavor.similar': 'Similar a tus favoritos',
  },
  fr: {
    'nav.locate': 'Localiser',
    'nav.humidor': 'Humidor',
    'nav.log': 'Journal',
    'nav.wishlist': 'Favoris',
    'nav.analytics': 'Analyses',
    'nav.journey': 'Voyage',
    'nav.sommelier': 'Sommelier',
    'nav.community': 'Communauté',
    'nav.profile': 'Profil',
    'nav.signOut': 'Déconnexion',
    'nav.gallery': 'Galerie',
    'dashboard.welcome': 'Bon retour',
    'common.loading': 'Chargement...',
    'common.save': 'Sauvegarder',
    'common.cancel': 'Annuler',
    'common.theme': 'Thème',
    'common.language': 'Langue',
    'common.dark': 'Sombre',
    'common.light': 'Clair',
    'logger.title': 'Enregistrez votre Expérience',
    'humidor.title': 'Humidor Numérique',
    'community.title': 'Fil de la Communauté',
    'notifications.title': 'Notifications',
    'gallery.title': 'Galerie Photo',
    'search.placeholder': 'Rechercher des cigares...',
    'offline.notice': 'Vous êtes hors ligne',
    'follow.follow': 'Suivre',
    'follow.following': 'Abonné',
  },
  de: {
    'nav.locate': 'Finden',
    'nav.humidor': 'Humidor',
    'nav.log': 'Protokoll',
    'nav.wishlist': 'Wunschliste',
    'nav.analytics': 'Analysen',
    'nav.journey': 'Reise',
    'nav.sommelier': 'Sommelier',
    'nav.community': 'Gemeinschaft',
    'nav.profile': 'Profil',
    'nav.signOut': 'Abmelden',
    'nav.gallery': 'Galerie',
    'dashboard.welcome': 'Willkommen zurück',
    'common.loading': 'Laden...',
    'common.save': 'Speichern',
    'common.cancel': 'Abbrechen',
    'common.theme': 'Thema',
    'common.language': 'Sprache',
    'common.dark': 'Dunkel',
    'common.light': 'Hell',
    'logger.title': 'Protokollieren Sie Ihre Erfahrung',
    'humidor.title': 'Digitaler Humidor',
    'community.title': 'Community-Feed',
    'notifications.title': 'Benachrichtigungen',
    'gallery.title': 'Fotogalerie',
    'search.placeholder': 'Zigarren suchen...',
    'offline.notice': 'Sie sind offline',
    'follow.follow': 'Folgen',
    'follow.following': 'Folgt',
  },
  it: {
    'nav.locate': 'Trova',
    'nav.humidor': 'Humidor',
    'nav.log': 'Registro',
    'nav.wishlist': 'Preferiti',
    'nav.analytics': 'Analisi',
    'nav.journey': 'Viaggio',
    'nav.sommelier': 'Sommelier',
    'nav.community': 'Comunità',
    'nav.profile': 'Profilo',
    'nav.signOut': 'Esci',
    'nav.gallery': 'Galleria',
    'dashboard.welcome': 'Bentornato',
    'common.loading': 'Caricamento...',
    'common.save': 'Salva',
    'common.cancel': 'Annulla',
    'common.theme': 'Tema',
    'common.language': 'Lingua',
    'common.dark': 'Scuro',
    'common.light': 'Chiaro',
    'logger.title': 'Registra la tua Esperienza',
    'humidor.title': 'Humidor Digitale',
    'community.title': 'Feed della Comunità',
    'notifications.title': 'Notifiche',
    'gallery.title': 'Galleria Fotografica',
    'search.placeholder': 'Cerca sigari...',
    'offline.notice': 'Sei offline',
    'follow.follow': 'Segui',
    'follow.following': 'Seguendo',
  },
  pt: {
    'nav.locate': 'Localizar',
    'nav.humidor': 'Humidor',
    'nav.log': 'Registro',
    'nav.wishlist': 'Favoritos',
    'nav.analytics': 'Análises',
    'nav.journey': 'Jornada',
    'nav.sommelier': 'Sommelier',
    'nav.community': 'Comunidade',
    'nav.profile': 'Perfil',
    'nav.signOut': 'Sair',
    'nav.gallery': 'Galeria',
    'dashboard.welcome': 'Bem-vindo de volta',
    'common.loading': 'Carregando...',
    'common.save': 'Salvar',
    'common.cancel': 'Cancelar',
    'common.theme': 'Tema',
    'common.language': 'Idioma',
    'common.dark': 'Escuro',
    'common.light': 'Claro',
    'logger.title': 'Registre sua Experiência',
    'humidor.title': 'Humidor Digital',
    'community.title': 'Feed da Comunidade',
    'notifications.title': 'Notificações',
    'gallery.title': 'Galeria de Fotos',
    'search.placeholder': 'Buscar charutos...',
    'offline.notice': 'Você está offline',
    'follow.follow': 'Seguir',
    'follow.following': 'Seguindo',
  },
  zh: {
    'nav.locate': '定位',
    'nav.humidor': '雪茄盒',
    'nav.log': '记录',
    'nav.wishlist': '心愿单',
    'nav.analytics': '分析',
    'nav.journey': '旅程',
    'nav.sommelier': '侍酒师',
    'nav.community': '社区',
    'nav.profile': '个人资料',
    'nav.signOut': '退出',
    'nav.gallery': '相册',
    'dashboard.welcome': '欢迎回来',
    'common.loading': '加载中...',
    'common.save': '保存',
    'common.cancel': '取消',
    'common.theme': '主题',
    'common.language': '语言',
    'common.dark': '深色',
    'common.light': '浅色',
    'logger.title': '记录你的体验',
    'humidor.title': '数字雪茄盒',
    'community.title': '社区动态',
    'notifications.title': '通知',
    'gallery.title': '照片库',
    'search.placeholder': '搜索雪茄...',
    'offline.notice': '您已离线',
    'follow.follow': '关注',
    'follow.following': '正在关注',
  },
  ja: {
    'nav.locate': '検索',
    'nav.humidor': 'ヒュミドール',
    'nav.log': '記録',
    'nav.wishlist': 'お気に入り',
    'nav.analytics': '分析',
    'nav.journey': '旅',
    'nav.sommelier': 'ソムリエ',
    'nav.community': 'コミュニティ',
    'nav.profile': 'プロフィール',
    'nav.signOut': 'ログアウト',
    'nav.gallery': 'ギャラリー',
    'dashboard.welcome': 'お帰りなさい',
    'common.loading': '読み込み中...',
    'common.save': '保存',
    'common.cancel': 'キャンセル',
    'common.theme': 'テーマ',
    'common.language': '言語',
    'common.dark': 'ダーク',
    'common.light': 'ライト',
    'logger.title': '体験を記録',
    'humidor.title': 'デジタルヒュミドール',
    'community.title': 'コミュニティフィード',
    'notifications.title': '通知',
    'gallery.title': 'フォトギャラリー',
    'search.placeholder': '葉巻を検索...',
    'offline.notice': 'オフラインです',
    'follow.follow': 'フォロー',
    'follow.following': 'フォロー中',
  },
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = safeStorageGet('aficionado_language');
    if (stored && Object.keys(translations).includes(stored)) {
      return stored as Language;
    }
    // Try to detect browser language
    const browserLang = typeof navigator !== 'undefined' ? navigator.language.split('-')[0] : 'en';
    if (Object.keys(translations).includes(browserLang)) {
      return browserLang as Language;
    }
    return 'en';
  });

  useEffect(() => {
    safeStorageSet('aficionado_language', language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string, params?: Record<string, string | number>): string => {
    let text = translations[language]?.[key] || translations['en']?.[key] || key;

    if (params) {
      Object.entries(params).forEach(([paramKey, value]) => {
        text = text.replace(`{${paramKey}}`, String(value));
      });
    }

    return text;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, languages }}>
      {children}
    </LanguageContext.Provider>
  );
};
